import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Images, CalendarCheck, FileText, Plus, Trash2, Save, LogOut,
  Upload, ExternalLink, Loader2, Pencil, X, Home, KeyRound, Euro,
} from "lucide-react";
import { api, errMsg, mediaUrl, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PriceAdmin } from "@/components/admin/PriceAdmin";

const TABS = [
  { id: "prices", label: "Cenu lapa", Icon: Euro },
  { id: "gallery", label: "Galerija", Icon: Images },
  { id: "bookings", label: "Pieteikumi", Icon: CalendarCheck },
  { id: "content", label: "Saturs", Icon: FileText },
  { id: "password", label: "Parole", Icon: KeyRound },
];

const STATUSES = ["jauns", "apstiprināts", "pabeigts", "atcelts"];
const field =
  "w-full border border-white/10 bg-black/50 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-rose-500";
const lbl = "font-mono-t text-[10px] uppercase tracking-[0.18em] text-slate-500";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("prices");
  const [gallery, setGallery] = useState([]);
  const [prices, setPrices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [content, setContent] = useState({});
  const [stats, setStats] = useState({});
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", repeat: "" });
  const [imgMeta, setImgMeta] = useState({ title: "", description: "" });

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.repeat) {
      toast.error("Jaunās paroles nesakrīt");
      return;
    }
    if (pw.new_password.trim().length < 8) {
      toast.error("Jaunajai parolei jābūt vismaz 8 simbolus garai");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", repeat: "" });
      toast.success("Parole nomainīta");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const loadAll = () => {
    api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {});
    api.get("/prices").then(({ data }) => setPrices(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  useEffect(loadAll, []);

  /* ---------------- gallery ---------------- */
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", imgMeta.title.trim());
    fd.append("description", imgMeta.description.trim());
    fd.append("category", "Darbi");
    setBusy(true);
    try {
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attēls augšupielādēts");
      setImgMeta({ title: "", description: "" });
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const addUrlImage = async () => {
    const url = window.prompt("Ievadi attēla URL:");
    if (!url) return;
    try {
      await api.post("/gallery/url", {
        external_url: url,
        title: imgMeta.title.trim(),
        description: imgMeta.description.trim(),
        category: "Darbi",
      });
      toast.success("Attēls pievienots");
      setImgMeta({ title: "", description: "" });
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const editCaption = async (g) => {
    const title = window.prompt("Nosaukums (daži vārdi):", g.title || "");
    if (title === null) return;
    const description = window.prompt("Īss apraksts:", g.description || "");
    if (description === null) return;
    try {
      await api.patch(`/gallery/${g.id}`, { title, description });
      toast.success("Attēla teksts saglabāts");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeImage = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Attēls dzēsts");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  /* ---------------- bookings ---------------- */
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success("Statuss atjaunots");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  /* ---------------- content ---------------- */
  const saveContent = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/content", content);
      setContent(data);
      toast.success("Saturs saglabāts");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080a]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#08080a]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-5 py-3 sm:px-8">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="TURBOLUX" className="h-9 w-auto object-contain" />
            <span className="hidden font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500 sm:block">
              Vadības centrs
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono-t text-[10px] text-slate-500 sm:block">{user?.username}</span>
            <Link
              to="/"
              data-testid="admin-view-site"
              className="flex h-9 items-center gap-2 border border-white/15 px-3 font-mono-t text-[10px] uppercase tracking-[0.15em] text-slate-300 hover:border-rose-500"
            >
              <Home className="h-3.5 w-3.5" /> Lapa
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex h-9 items-center gap-2 border border-rose-600/50 bg-rose-600/10 px-3 font-mono-t text-[10px] uppercase tracking-[0.15em] text-rose-300 hover:bg-rose-600 hover:text-white"
            >
              <LogOut className="h-3.5 w-3.5" /> Iziet
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] px-5 py-8 sm:px-8">
        <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4">
          {[
            { l: "Cenrāža sadaļas", v: prices.length },
            { l: "Galerijas attēli", v: stats.gallery ?? gallery.length },
            { l: "Pieteikumi", v: stats.bookings ?? bookings.length },
            { l: "Jauni pieteikumi", v: stats.new_bookings ?? 0 },
          ].map((s) => (
            <div key={s.l} className="bg-[#0c0c11] px-5 py-5" data-testid={`admin-stat-${s.l}`}>
              <div className="font-head text-3xl font-extrabold text-white">{s.v}</div>
              <div className="mt-1 font-mono-t text-[9px] uppercase tracking-[0.18em] text-slate-500">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 border px-4 py-2.5 font-mono-t text-[10px] uppercase tracking-[0.18em] transition-all ${
                tab === t.id
                  ? "border-rose-600 bg-rose-600/15 text-rose-300"
                  : "border-white/10 text-slate-500 hover:border-white/30 hover:text-slate-200"
              }`}
            >
              <t.Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-6">

          {/* ---------------- GALLERY ---------------- */}
          {tab === "prices" && <PriceAdmin categories={prices} onReload={loadAll} />}

          {tab === "gallery" && (
            <div>
              <div className="metal-panel mb-5 grid gap-4 p-5 sm:grid-cols-2">
                <label className="block">
                  <span className={lbl}>Nosaukums (daži vārdi)</span>
                  <input
                    value={imgMeta.title}
                    onChange={(e) => setImgMeta({ ...imgMeta, title: e.target.value })}
                    placeholder="BMW 535d metināšanas darbi"
                    data-testid="admin-image-title"
                    className={`mt-2 ${field}`}
                  />
                </label>
                <label className="block">
                  <span className={lbl}>Īss apraksts</span>
                  <input
                    value={imgMeta.description}
                    onChange={(e) => setImgMeta({ ...imgMeta, description: e.target.value })}
                    placeholder="Jauna turbīna, 2 dienas darbnīcā"
                    data-testid="admin-image-description"
                    className={`mt-2 ${field}`}
                  />
                </label>
                <p className="font-mono-t text-[9px] uppercase tracking-[0.18em] text-slate-500 sm:col-span-2">
                  Aizpildi pirms augšupielādes — teksts parādīsies zem attēla mājaslapā.
                </p>
              </div>
              <div className="mb-5 flex flex-wrap gap-3">
                <label
                  data-testid="admin-upload-label"
                  className="flex cursor-pointer items-center gap-2 bg-rose-600 px-5 py-3 font-head text-base font-bold uppercase text-white hover:bg-rose-500"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Augšupielādēt attēlu
                  <input type="file" accept="image/*" onChange={uploadImage} data-testid="admin-upload-input" className="hidden" />
                </label>
                <button
                  onClick={addUrlImage}
                  data-testid="admin-add-url-btn"
                  className="flex items-center gap-2 border border-white/15 px-5 py-3 font-head text-base font-bold uppercase text-slate-200 hover:border-rose-500"
                >
                  <ExternalLink className="h-4 w-4" /> Pievienot pēc URL
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-gallery-grid">
                {gallery.map((g) => (
                  <div key={g.id} className="group relative overflow-hidden border border-white/10">
                    <img src={mediaUrl(g.url)} alt={g.title} className="h-44 w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-black/75 p-3">
                      <div className="truncate font-mono-t text-[9px] uppercase tracking-[0.15em] text-slate-300">
                        {g.title || "Bez nosaukuma"}
                      </div>
                      {g.description && (
                        <div className="truncate text-[10px] text-slate-500">{g.description}</div>
                      )}
                    </div>
                    <button
                      onClick={() => editCaption(g)}
                      data-testid={`admin-edit-image-${g.id}`}
                      className="absolute right-11 top-2 flex h-8 w-8 items-center justify-center border border-white/20 bg-black/70 text-slate-200 opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeImage(g.id)}
                      data-testid={`admin-delete-image-${g.id}`}
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center border border-white/20 bg-black/70 text-rose-400 opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {gallery.length === 0 && (
                  <p className="font-mono-t text-xs uppercase tracking-widest text-slate-500">
                    Vēl nav pievienots neviens attēls.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ---------------- BOOKINGS ---------------- */}
          {tab === "bookings" && (
            <div className="space-y-px bg-white/10" data-testid="admin-bookings-list">
              {bookings.map((b) => (
                <div key={b.id} className="bg-[#0c0c11] p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="font-mono-t text-[9px] uppercase tracking-[0.2em] text-rose-500">{b.reference}</div>
                      <div className="font-head text-xl font-bold uppercase text-white">{b.name}</div>
                      <div className="mt-1 text-xs text-slate-400">
                        {b.phone} {b.email && `· ${b.email}`}
                      </div>
                      <div className="mt-2 text-sm text-slate-300">
                        {[b.car_make, b.car_model, b.car_year, b.mileage].filter(Boolean).join(" · ")}
                      </div>
                      {b.service && <div className="mt-1 text-sm text-rose-300">{b.service}</div>}
                      {b.preferred_date && (
                        <div className="mt-1 font-mono-t text-[10px] text-slate-500">Vēlamais: {b.preferred_date}</div>
                      )}
                      {b.message && <p className="mt-2 max-w-xl text-xs text-slate-500">{b.message}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          onClick={() => setStatus(b.id, s)}
                          data-testid={`admin-booking-${b.id}-status-${s}`}
                          className={`border px-3 py-1.5 font-mono-t text-[9px] uppercase tracking-[0.15em] transition-all ${
                            b.status === s
                              ? "border-rose-600 bg-rose-600/20 text-rose-200"
                              : "border-white/10 text-slate-500 hover:border-white/30"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {bookings.length === 0 && (
                <p className="bg-[#0c0c11] p-5 font-mono-t text-xs uppercase tracking-widest text-slate-500">
                  Vēl nav neviena pieteikuma.
                </p>
              )}
            </div>
          )}

          {/* ---------------- CONTENT ---------------- */}
          {tab === "content" && (
            <div className="metal-panel grid gap-4 p-6 sm:grid-cols-2" data-testid="admin-content-form">
              {Object.keys(content)
                .filter((k) => k !== "_id")
                .map((k) => (
                  <label key={k} className={k.includes("text") || k.includes("subtitle") || k === "announcement" ? "block sm:col-span-2" : "block"}>
                    <span className={lbl}>{k.replace(/_/g, " ")}</span>
                    {k.includes("text") || k.includes("subtitle") || k === "announcement" ? (
                      <textarea
                        rows={3}
                        value={content[k] || ""}
                        onChange={(e) => setContent({ ...content, [k]: e.target.value })}
                        data-testid={`admin-content-${k}`}
                        className={`mt-2 resize-none ${field}`}
                      />
                    ) : (
                      <input
                        value={content[k] || ""}
                        onChange={(e) => setContent({ ...content, [k]: e.target.value })}
                        data-testid={`admin-content-${k}`}
                        className={`mt-2 ${field}`}
                      />
                    )}
                  </label>
                ))}
              <button
                onClick={saveContent}
                disabled={busy}
                data-testid="admin-content-save"
                className="flex items-center justify-center gap-2 bg-rose-600 py-3 font-head text-base font-bold uppercase text-white hover:bg-rose-500 disabled:opacity-60 sm:col-span-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Saglabāt saturu
              </button>
            </div>
          )}
          {/* ---------------- PASSWORD ---------------- */}
          {tab === "password" && (
            <form onSubmit={savePassword} data-testid="admin-password-form" className="metal-panel max-w-lg p-6 sm:p-8">
              <div className="font-head text-xl font-bold uppercase text-white">Mainīt paroli</div>
              <p className="mt-2 text-xs text-slate-500">
                Lietotājvārds <span className="font-mono-t text-rose-400">{user?.username}</span> paliek nemainīgs.
                Jaunā parole — vismaz 8 simboli.
              </p>
              <div className="mt-6 space-y-4">
                {[
                  { k: "current_password", l: "Pašreizējā parole", tid: "admin-password-current" },
                  { k: "new_password", l: "Jaunā parole", tid: "admin-password-new" },
                  { k: "repeat", l: "Atkārto jauno paroli", tid: "admin-password-repeat" },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className={lbl}>{f.l}</span>
                    <input
                      type="password"
                      autoComplete="off"
                      value={pw[f.k]}
                      onChange={(e) => setPw({ ...pw, [f.k]: e.target.value })}
                      data-testid={f.tid}
                      className={`mt-2 ${field}`}
                      required
                    />
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="admin-password-save"
                className="mt-6 flex w-full items-center justify-center gap-2 bg-rose-600 py-3 font-head text-base font-bold uppercase text-white hover:bg-rose-500 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Saglabāt paroli
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
